import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { CommonService } from './common.service';
import type {
    PredefinedChartApiResponse,
    PredefinedChartRequest,
    PredefinedChartDefinition,
    BatchPredefinedChartsResponse,
    PredefinedChartData,
    PredefinedChartSeriesItem,
} from '../shared/predefined-charts.types';
import type { FilterRequest } from '../shared/types';
import { normalizeFilterRequestsForBackend } from '../shared/appUtil';

const PREDEFINED_CHARTS_BASE = 'trends/predefined-charts';
const BATCH_BASE = 'trends/batch-predefined-charts/batch';

/** Hourly bucket options for single-day primary topic / intent area charts */
export const BUCKET_GRANULARITY_OPTIONS: { label: string; value: number }[] = [
  { label: '1 hr', value: 1 },
  { label: '2 hr', value: 2 },
  { label: '4 hr', value: 4 },
  { label: '8 hr', value: 8 },
  { label: '12 hr', value: 12 },
];

export const DEFAULT_BUCKET_GRANULARITY_HOURS = 4;

/** Request body for chart view combined API (same as data-explorer chart view) */
export interface ChartViewCombinedRequestBody {
  start_date: string;
  end_date: string;
  filters: FilterRequest[];
  timeZone?: string;
  /** Time bucket size in hours when start_date === end_date (primary_topic + intent area charts) */
  bucket_granularity_hours?: number;
}

/** Response shape from get-data/combined_topic_resolution_data (chart view API) */
export interface ChartViewCombinedResponse {
  primary_topic_distribution?: { series?: PredefinedChartSeriesItem[] };
  primary_topic?: { series?: PredefinedChartSeriesItem[]; dates?: string[] };
  resolution_distribution?: { series?: unknown[] };
  intent?: { series?: unknown[]; dates?: string[] };
  /** Intent distribution (same shape as primary_topic_distribution) for Customer Intent Distribution charts */
  intent_distribution?: { series?: PredefinedChartSeriesItem[] };
}

/** Area charts (first two) use chart view API only: get-data/combined_topic_resolution_data. Bar/distribution/sankey use batch APIs. */
export const PREDEFINED_CHART_DEFINITIONS: PredefinedChartDefinition[] = [
  { id: 'area-primary-topics', endpoint: `${PREDEFINED_CHARTS_BASE}/area/primary-topics-distribution`, title: 'Primary Topics Over Time', apiKey: 'area_primary_topics' },
  { id: 'area-customer-intent', endpoint: `${PREDEFINED_CHARTS_BASE}/area/customer-intent-distribution`, title: 'Customer Intents Over Time', apiKey: 'area_customer_intent' },
  { id: 'bar-primary-topic', endpoint: `${PREDEFINED_CHARTS_BASE}/bar/primary-topic`, title: 'Top 10 Primary Topic Distribution', apiKey: 'bar_primary_topic', batchEndpoint: 'bar-metrics' },
  { id: 'bar-customer-intent', endpoint: `${PREDEFINED_CHARTS_BASE}/bar/customer-intent`, title: 'Top 10 Customer Intent Distribution', apiKey: 'bar_customer_intent', batchEndpoint: 'bar-metrics' },
  { id: 'pie-call-resolution', endpoint: `${PREDEFINED_CHARTS_BASE}/pie/call-resolution`, title: 'Call Resolution Analysis', apiKey: 'pie_call_resolution', batchEndpoint: 'distribution-metrics' },
  { id: 'pie-journey-moment', endpoint: `${PREDEFINED_CHARTS_BASE}/pie/journey-moment-distribution`, title: 'Journey Moment Distribution', apiKey: 'pie_journey_moment', batchEndpoint: 'distribution-metrics' },
  { id: 'donut-caller-type', endpoint: `${PREDEFINED_CHARTS_BASE}/donut/caller-type`, title: 'Caller Type Distribution', apiKey: 'donut_caller_type', batchEndpoint: 'distribution-metrics' },
  { id: 'sankey-sentiment', endpoint: `${PREDEFINED_CHARTS_BASE}/sankey/sentiment-positive-to-negative`, title: 'Customer Sentiment Analysis', apiKey: 'sankey_sentiment', batchEndpoint: 'sankey-metrics' },
  { id: 'sankey-topic-intent', endpoint: `${PREDEFINED_CHARTS_BASE}/sankey/primary-topic-to-customer-intent`, title: 'Top 10 Primary Topic to Customer Intent Relationship', apiKey: 'sankey_topic_intent', batchEndpoint: 'sankey-metrics' },
];

@Injectable({
  providedIn: 'root',
})
export class PredefinedChartsService {
  constructor(private commonService: CommonService) {}

  /**
   * Build request payload from dashboard date range, filters and timezone.
   */
  buildRequest(payload: {
    start_date: string;
    end_date: string;
    filters: FilterRequest[];
    timeZone: string;
  }): PredefinedChartRequest {
    return {
      start_date: payload.start_date,
      end_date: payload.end_date,
      filters: normalizeFilterRequestsForBackend(payload.filters ?? []),
      timeZone: payload.timeZone || 'UTC',
    };
  }

  /**
   * Call a single predefined chart endpoint (once, no retries).
   */
  fetchChart(
    endpoint: string,
    request: PredefinedChartRequest
  ): Observable<PredefinedChartApiResponse | null> {
    return this.commonService.postRequest(endpoint, request).pipe(
      catchError((err) => {
        console.error(`PredefinedChartsService failed for ${endpoint}:`, err);
        return of(null);
      })
    );
  }

  /**
   * Fetch one chart by definition.
   */
  fetchChartByDefinition(
    definition: PredefinedChartDefinition,
    request: PredefinedChartRequest
  ): Observable<PredefinedChartApiResponse | null> {
    return this.fetchChart(definition.endpoint, request);
  }

  /**
   * Get all chart definitions (for grid layout).
   */
  getChartDefinitions(): PredefinedChartDefinition[] {
    return [...PREDEFINED_CHART_DEFINITIONS];
  }

  /**
   * Fetch charts from a single batch endpoint (bar-metrics, distribution-metrics, or sankey-metrics).
   */
  fetchBatchCharts(
    batchEndpoint: 'bar-metrics' | 'distribution-metrics' | 'sankey-metrics',
    request: PredefinedChartRequest
  ): Observable<BatchPredefinedChartsResponse | null> {
    const endpoint = `${BATCH_BASE}/${batchEndpoint}`;

    return this.commonService.postRequest(endpoint, request).pipe(
      catchError((err) => {
        console.error(`PredefinedChartsService batch fetch failed (${batchEndpoint}):`, err);
        return of(null);
      })
    );
  }

  /**
   * Fetch chart view combined data (same API and structure as data-explorer chart view).
   * Used only for the two area charts: Primary Topics Over Time and Customer Intents Over Time.
   * All other predefined charts use the batch predefined chart API.
   */
  fetchChartViewCombinedData(body: ChartViewCombinedRequestBody): Observable<ChartViewCombinedResponse | null> {
    return this.commonService.fetchCombinedPercentageDistributionChart(body).pipe(
      catchError((err) => {
        console.error('PredefinedChartsService chart view combined API failed:', err);
        return of(null);
      })
    );
  }

  /**
   * Map chart view combined API response to PredefinedChartData.
   * Used only for the two area charts: Primary Topics Over Time and Customer Intents Over Time.
   * All other charts (bar, pie, donut, sankey) use predefined chart API (batch) data only.
   */
  mapCombinedResponseToPredefinedChartData(
    response: ChartViewCombinedResponse | null,
    chartKey: 'area_primary_topics' | 'area_customer_intent'
  ): PredefinedChartData | null {
    if (!response) return null;

    const now = new Date().toISOString();

    if (chartKey === 'area_primary_topics') {
      const series = response.primary_topic?.series ?? [];
      const dates = response.primary_topic?.dates ?? [];
      const noData = !series.length;
      return {
        ChartId: 'area-primary-topics',
        ChartType: 'area',
        MetricType: 'percentage',
        ChartConfig: {
          chart: { type: 'area' },
          xAxis: { categories: dates },
          yAxis: {
            max: 100,
            title: { text: 'Percentage' },
            tickPositions: [0, 20, 40, 60, 80, 100],
            labels: { format: '{value}%' },
          },
          tooltip: { valueSuffix: '%' },
          plotOptions: { area: { stacking: 'percent' } },
          credits: { enabled: false },
          exporting: { enabled: false },
        },
        Series: series.map((s) => ({
          name: s.name,
          data: Array.isArray(s.data) ? s.data : [],
        })),
        Categories: dates.length ? dates : null,
        Description: {
          title: 'Primary Topics Over Time',
          chartType: 'area',
          metricType: 'percentage',
          generatedAt: now,
          isPredefined: true,
        },
        NoDataFound: noData,
      };
    }

    if (chartKey === 'area_customer_intent') {
      const series = response.intent?.series ?? [];
      const dates = response.intent?.dates ?? [];
      const noData = !series.length;
      const seriesMapped = (series as PredefinedChartSeriesItem[]).map((s) => ({
        name: s.name,
        data: Array.isArray(s.data) ? s.data : [],
      }));
      return {
        ChartId: 'area-customer-intent',
        ChartType: 'area',
        MetricType: 'percentage',
        ChartConfig: {
          chart: { type: 'area' },
          xAxis: { categories: dates },
          yAxis: {
            max: 100,
            title: { text: 'Percentage' },
            tickPositions: [0, 20, 40, 60, 80, 100],
            labels: { format: '{value}%' },
          },
          tooltip: { valueSuffix: '%' },
          plotOptions: { area: { stacking: 'percent' } },
          credits: { enabled: false },
          exporting: { enabled: false },
        },
        Series: seriesMapped,
        Categories: dates.length ? dates : null,
        Description: {
          title: 'Customer Intents Over Time',
          chartType: 'area',
          metricType: 'percentage',
          generatedAt: now,
          isPredefined: true,
        },
        NoDataFound: noData,
      };
    }

    return null;
  }
}
